import { defaultClone } from "../lib/clone"
import { normalizeConfigBase } from "../lib/config"
import { ConfigError } from "../lib/errors"
import { validateConfig, validateProblem } from "../lib/validation"
import { mulberry32 } from "../lib/rng"
import type {
  GPConfig,
  GPHooks,
  GPMetrics,
  GPProblem,
  GPResult,
  OnGeneration,
  RNG,
  Selector,
  StopReason,
} from "../types"
import { tournament } from "./selection"
import { FitnessCache } from "../lib/fitness-cache"
import { computePopulationStats } from "../lib/stats"
import { buildRunMetrics } from "../lib/metrics"

type EngineAsyncConfig<T> = ReturnType<typeof normalizeConfigBase<T>> & {
  rng: RNG
  selector: Selector<T>
  hooks: GPHooks<T>
  clone: (g: Readonly<T>) => T
  cacheFitness: boolean
  fitnessKey?: (g: Readonly<T>) => string
  signal?: AbortSignal
  fitnessTimeoutMs: number
  initialPopulation?: ReadonlyArray<T>
}

/**
 * Asynchronous GP/GA engine.
 *
 * Mirrors the synchronous engine but allows async `createRandom`, `fitness`,
 * `mutate`, and `crossover`. Concurrency controls the number of in-flight tasks.
 *
 * Key points for newcomers:
 * - Pc + Pm = 1 (immigration handled separately after breeding).
 * - Per-evaluation timeout (`fitnessTimeoutMs`) marks fitness as invalid (-Infinity).
 * - Budgets (`maxWallMs`, `maxEvaluations`) are respected between awaited phases.
 */
export class GPLiteAsync<T> {
  readonly cfg: EngineAsyncConfig<T>
  readonly rng: RNG
  private select: Selector<T>
  // Fitness memoization helper
  private fitnessCache: FitnessCache<T>

  constructor(
    private problem: GPProblem<T>,
    cfg: GPConfig<T> = {},
  ) {
    validateProblem(problem)
    validateConfig(cfg)
    const base = normalizeConfigBase(cfg)
    this.cfg = {
      ...base,
      rng:
        cfg.rng ??
        (cfg.seed !== undefined ? mulberry32(cfg.seed) : mulberry32()),
      selector: cfg.selector ?? tournament(base.tournament),
      hooks: cfg.hooks ?? {},
      clone: cfg.clone ?? defaultClone,
      cacheFitness: cfg.cacheFitness ?? false,
      fitnessKey: cfg.fitnessKey,
      signal: cfg.signal,
      fitnessTimeoutMs: cfg.fitnessTimeoutMs ?? Infinity,
      initialPopulation: cfg.initialPopulation,
    }
    this.rng = this.cfg.rng
    this.select = this.cfg.selector
    // Setup optional fitness cache
    this.fitnessCache = new FitnessCache<T>({
      enabled: !!this.cfg.cacheFitness,
      fitnessKey: this.cfg.fitnessKey,
    })
    if (this.cfg.elite > this.cfg.popSize) {
      throw new ConfigError("Elite count cannot exceed population size")
    }
  }

  static seed(seed: number) {
    return mulberry32(seed)
  }

  private async safeFitness(
    g: T,
    metrics?: Pick<
      GPMetrics,
      | "evaluations"
      | "invalidEvaluations"
      | "repaired"
      | "repairFailures"
      | "fitnessErrors"
      | "nonFiniteFitness"
    >,
  ): Promise<number> {
    let genome = g
    if (metrics) metrics.evaluations += 1

    // Cache lookup first
    {
      const got = this.fitnessCache.get(genome)
      if (got !== undefined) return got
    }

    try {
      if (
        this.problem.isValid &&
        !(await this.problem.isValid(genome, { signal: this.cfg.signal }))
      ) {
        if (this.problem.repair) {
          genome = await this.problem.repair(genome, this.rng, {
            signal: this.cfg.signal,
          })
          if (
            this.problem.isValid &&
            !(await this.problem.isValid(genome, { signal: this.cfg.signal }))
          ) {
            if (metrics) {
              metrics.invalidEvaluations += 1
              metrics.repairFailures += 1
            }
            return -Infinity
          }
          if (metrics) metrics.repaired += 1
        } else {
          if (metrics) metrics.invalidEvaluations += 1
          return -Infinity
        }
      }

      // Compose per-evaluation timeout with external abort signal
      const timeoutMs = this.cfg.fitnessTimeoutMs
      const parent = this.cfg.signal
      let timer: ReturnType<typeof setTimeout> | undefined
      const controller = new AbortController()
      const onParentAbort = () => controller.abort(parent?.reason)
      if (parent?.aborted) controller.abort(parent.reason)
      else if (parent) parent.addEventListener("abort", onParentAbort)
      const fitnessP = Promise.resolve(
        this.problem.fitness(genome, { signal: controller.signal }),
      ).catch(() => -Infinity)
      const timeoutP = new Promise<number>((resolve) => {
        if (Number.isFinite(timeoutMs) && timeoutMs! >= 0) {
          timer = setTimeout(() => {
            try {
              controller.abort(new Error("fitness timeout"))
            } catch {}
            resolve(-Infinity)
          }, timeoutMs)
        }
      })

      const f =
        Number.isFinite(timeoutMs) && timeoutMs! >= 0
          ? await Promise.race([fitnessP, timeoutP])
          : await fitnessP

      if (timer) clearTimeout(timer)
      if (parent) parent.removeEventListener("abort", onParentAbort)
      if (!Number.isFinite(f) || f !== f) {
        if (metrics) {
          metrics.invalidEvaluations += 1
          metrics.nonFiniteFitness += 1
        }
        return -Infinity
      }
      // Cache finite values only
      this.fitnessCache.set(genome, f)
      return f
    } catch {
      if (metrics) {
        metrics.invalidEvaluations += 1
        metrics.fitnessErrors += 1
      }
      return -Infinity
    }
  }

  /**
   * Execute the evolutionary loop (asynchronous).
   *
   * Note on callbacks: `onGen` is a convenience alias to observe per-generation
   * progress. For most integrations, prefer the structured lifecycle hooks via
   * `new GPLiteAsync(problem, { hooks: { ... } })` — e.g. `onGenerationStart`,
   * `onGenerationEnd`, or `onIteration` — to keep the problem definition pure
   * and avoid coupling domain logic with progress I/O.
   */
  async run(onGen?: OnGeneration<T>): Promise<GPResult<T>> {
    const t0 = Date.now()
    const timeExceeded = () => Date.now() - t0 >= this.cfg.maxWallMs
    let stopReason: StopReason | undefined

    const metrics = {
      evaluations: 0,
      invalidEvaluations: 0,
      repaired: 0,
      repairFailures: 0,
      fitnessErrors: 0,
      nonFiniteFitness: 0,
      mutations: 0,
      crossovers: 0,
      selections: 0,
      immigrants: 0,
    }

    const baseSlots = Math.max(0, this.cfg.popSize - this.cfg.elite)
    const immigrantsPerGen = Math.min(
      Math.round(baseSlots * this.cfg.immigration),
      baseSlots,
    )

    let pop: Array<{ g: T; f: number }> = []
    // Build initial population with concurrency, honoring budgets; support optional initialPopulation
    {
      const target = this.cfg.popSize
      const seedGenomes: ReadonlyArray<T> | undefined =
        this.cfg.initialPopulation
      let seedIdx = 0
      const inFlight: Array<Promise<{ g: T; f: number }>> = []
      const schedule = () => {
        const task = (async () => {
          const g =
            seedGenomes && seedIdx < seedGenomes.length
              ? (seedGenomes[seedIdx++] as T)
              : await this.problem.createRandom(this.rng, {
                  signal: this.cfg.signal,
                })
          const f = await this.safeFitness(g, metrics)
          return { g, f }
        })()
        inFlight.push(task)
      }

      while (pop.length < target) {
        if (this.cfg.signal?.aborted) {
          stopReason = "aborted"
          break
        }
        if (timeExceeded()) {
          stopReason = "time"
          break
        }
        const remainingSlots = target - pop.length - inFlight.length
        const remainingEvals =
          this.cfg.maxEvaluations - metrics.evaluations - inFlight.length
        if (remainingEvals <= 0) {
          stopReason = "evaluations"
          break
        }
        const canStart = Math.min(
          Math.max(0, this.cfg.concurrency - inFlight.length),
          Math.max(0, remainingSlots),
          Math.max(0, remainingEvals),
        )
        for (let i = 0; i < canStart; i++) schedule()
        if (!inFlight.length) break
        const idx = await Promise.race(inFlight.map((p, i) => p.then(() => i)))
        const done = inFlight.splice(idx, 1)[0]
        const item = await done
        pop.push(item)
      }
    }

    if (
      stopReason ||
      metrics.evaluations >= this.cfg.maxEvaluations ||
      timeExceeded()
    ) {
      pop.sort((a, b) => b.f - a.f)
      const head = pop[0] ?? ({} as { g: T; f: number })
      return {
        bestGenome: head?.g ?? (undefined as unknown as T),
        bestFitness: head?.f ?? -Infinity,
        generations: 0,
        bestFitnessHistory: pop.length ? [head.f] : [],
        bestGenomeHistory: pop.length ? [head.g] : [],
        elapsedMs: Date.now() - t0,
        stopReason:
          stopReason ??
          (metrics.evaluations >= this.cfg.maxEvaluations
            ? "evaluations"
            : "time"),
        avgFitnessHistory: pop.length ? [-Infinity] : [],
        invalidCountHistory: pop.length ? [0] : [],
        validShareHistory: pop.length ? [1] : [],
        metrics: buildRunMetrics(metrics, {
          popSize: this.cfg.popSize,
          generations: this.cfg.generations,
          cxProb: this.cfg.cxProb,
          mutProb: this.cfg.mutProb,
          crossoverChildren: this.cfg.crossoverChildren,
          immigration: this.cfg.immigration,
          tournament: this.cfg.tournament,
          stall: this.cfg.stall,
          targetFitness: this.cfg.targetFitness,
          maxWallMs: this.cfg.maxWallMs,
          maxEvaluations: this.cfg.maxEvaluations,
          elitesPerGen: this.cfg.elite,
        }),
      }
    }

    const history: number[] = []
    const meanHistory: number[] = []
    const invalidHistory: number[] = []
    const validShareHistory: number[] = []
    const bestGenomeHistory: T[] = []

    for (let gen = 0; gen < this.cfg.generations; ++gen) {
      this.cfg.hooks.onGenerationStart?.({
        generation: gen,
        popSize: this.cfg.popSize,
        elapsedMs: Date.now() - t0,
      })
      pop.sort((a, b) => b.f - a.f)

      const { best, mean, invalidCount, finiteCount } = computePopulationStats(
        pop,
      )
      history.push(best)
      meanHistory.push(mean)
      invalidHistory.push(invalidCount)
      validShareHistory.push(finiteCount / pop.length)
      bestGenomeHistory.push(pop[0].g)
      // Determine stop reason prior to emitting callbacks to surface it
      let nextStop: StopReason | undefined
      if (this.cfg.signal?.aborted) nextStop = "aborted"
      else if (best >= this.cfg.targetFitness) nextStop = "target"
      else if (Date.now() - t0 >= this.cfg.maxWallMs) nextStop = "time"
      else if (metrics.evaluations >= this.cfg.maxEvaluations)
        nextStop = "evaluations"
      else {
        const w = this.cfg.stall
        if (
          w &&
          history.length > w &&
          history[history.length - 1] <= history[history.length - 1 - w]
        ) {
          nextStop = "stall"
        }
      }

      const payload = {
        generation: gen,
        bestFitness: best,
        avgFitness: mean,
        popSize: pop.length,
        invalidCount,
        validShare: finiteCount / pop.length,
        bestGenome: pop[0].g,
        elapsedMs: Date.now() - t0,
        stopReason: nextStop,
      }
      onGen?.(payload)
      this.cfg.hooks.onGenerationEnd?.(payload)
      // alias progress hook
      this.cfg.hooks.onIteration?.(payload)

      if (nextStop === "aborted") {
        stopReason = "aborted"
        break
      }
      if (nextStop === "target") {
        stopReason = "target"
        break
      }
      if (nextStop === "time") {
        stopReason = "time"
        break
      }
      if (nextStop === "evaluations") {
        stopReason = "evaluations"
        break
      }
      if (nextStop === "stall") {
        stopReason = "stall"
        break
      }

      const next: typeof pop = pop.slice(0, this.cfg.elite)

      // Proportions: allocate fixed counts for children
      const targetChildren = this.cfg.popSize - immigrantsPerGen
      const slots = targetChildren - next.length
      // We already reserved immigrant slots via targetChildren; do not subtract again here.
      const cxChildrenTarget = Math.min(
        slots,
        Math.round((this.cfg.popSize - this.cfg.elite) * this.cfg.cxProb),
      )
      const mutChildrenTarget = Math.max(0, slots - cxChildrenTarget)

      // Concurrency-limited child creation/evaluation for crossover children
      {
        let produced = 0
        const inFlight: Array<Promise<Array<{ g: T; f: number }>>> = []
        const scheduleCrossover = (maxChildren: number) => {
          const task = (async () => {
            const p1 = this.select(pop, this.rng)
            const p2 = this.select(pop, this.rng)
            metrics.selections += 2
            const pa = this.cfg.clone(pop[p1].g)
            const pb = this.cfg.clone(pop[p2].g)
            metrics.crossovers += 1
            const children = await this.problem.crossover(pa, pb, this.rng, {
              signal: this.cfg.signal,
            })

            const out: Array<{ g: T; f: number }> = []
            const numToTake = Math.min(children.length, maxChildren)
            for (let i = 0; i < numToTake; i++) {
              let child = children[i]
              if (child === pa || child === pb) child = this.cfg.clone(child)
              out.push({ g: child, f: await this.safeFitness(child, metrics) })
            }
            return out
          })()
          inFlight.push(task)
        }

        while (produced < cxChildrenTarget) {
          if (timeExceeded()) {
            stopReason = "time"
            break
          }
          if (this.cfg.signal?.aborted) {
            stopReason = "aborted"
            break
          }
          const remainingEvals = this.cfg.maxEvaluations - metrics.evaluations
          if (remainingEvals <= 0) {
            stopReason = "evaluations"
            break
          }
          const canStartTasks = this.cfg.concurrency - inFlight.length
          if (canStartTasks > 0) {
            const remaining = cxChildrenTarget - produced
            const crossoversNeeded = Math.ceil(
              remaining / this.cfg.crossoverChildren,
            )
            const startNow = Math.min(canStartTasks, crossoversNeeded)
            for (let i = 0; i < startNow; i++) {
              const left = cxChildrenTarget - produced
              const maxChildren = Math.min(left, this.cfg.crossoverChildren)
              scheduleCrossover(maxChildren)
              produced += maxChildren
            }
          }
          if (!inFlight.length) break
          const idx = await Promise.race(
            inFlight.map((p, i) => p.then(() => i)),
          )
          const done = inFlight.splice(idx, 1)[0]
          const res = await done
          const slotsLeft = targetChildren - next.length
          for (let i = 0; i < Math.min(slotsLeft, res.length); i++)
            next.push(res[i])
        }
        if (stopReason) {
          pop = next
          break
        }
      }

      // Mutation children
      {
        const inFlight: Array<Promise<{ g: T; f: number }>> = []
        const scheduleMut = () => {
          const task = (async () => {
            const p = this.select(pop, this.rng)
            metrics.selections += 1
            const before = this.cfg.clone(pop[p].g)
            let child: T = (await this.problem.mutate(before, this.rng, {
              signal: this.cfg.signal,
            })) as T
            if (child === before) child = this.cfg.clone(child)
            metrics.mutations += 1
            return { g: child, f: await this.safeFitness(child, metrics) }
          })()
          inFlight.push(task)
        }
        let produced = 0
        while (produced < mutChildrenTarget && next.length < targetChildren) {
          if (timeExceeded()) {
            stopReason = "time"
            break
          }
          if (this.cfg.signal?.aborted) {
            stopReason = "aborted"
            break
          }
          const remainingEvals = this.cfg.maxEvaluations - metrics.evaluations
          if (remainingEvals <= 0) {
            stopReason = "evaluations"
            break
          }
          const canStart = this.cfg.concurrency - inFlight.length
          for (
            let i = 0;
            i < Math.min(canStart, mutChildrenTarget - produced);
            i++
          ) {
            scheduleMut()
            produced += 1
          }
          if (!inFlight.length) break
          const idx = await Promise.race(
            inFlight.map((p, i) => p.then(() => i)),
          )
          const done = inFlight.splice(idx, 1)[0]
          const item = await done
          if (next.length < targetChildren) next.push(item)
        }
        if (stopReason) {
          pop = next
          break
        }
      }
      if (stopReason) {
        pop = next
        break
      }

      for (let i = 0; i < immigrantsPerGen; ++i) {
        if (timeExceeded()) {
          stopReason = "time"
          break
        }
        if (this.cfg.signal?.aborted) {
          stopReason = "aborted"
          break
        }
        const g = await this.problem.createRandom(this.rng, {
          signal: this.cfg.signal,
        })
        next.push({ g, f: await this.safeFitness(g, metrics) })
        metrics.immigrants += 1
        if (metrics.evaluations >= this.cfg.maxEvaluations) {
          stopReason = "evaluations"
          break
        }
      }
      if (stopReason) {
        pop = next
        break
      }

      pop = next
    }

    pop.sort((a, b) => b.f - a.f)
    if (!stopReason) stopReason = "generations"
    const head = pop[0] ?? ({} as { g: T; f: number })
    return {
      bestGenome: head?.g ?? (undefined as unknown as T),
      bestFitness: head?.f ?? -Infinity,
      generations: history.length,
      bestFitnessHistory: history,
      bestGenomeHistory,
      elapsedMs: Date.now() - t0,
      stopReason,
      avgFitnessHistory: meanHistory,
      invalidCountHistory: invalidHistory,
      validShareHistory,
      metrics: buildRunMetrics(metrics, {
        popSize: this.cfg.popSize,
        generations: this.cfg.generations,
        cxProb: this.cfg.cxProb,
        mutProb: this.cfg.mutProb,
        crossoverChildren: this.cfg.crossoverChildren,
        immigration: this.cfg.immigration,
        tournament: this.cfg.tournament,
        stall: this.cfg.stall,
        targetFitness: this.cfg.targetFitness,
        maxWallMs: this.cfg.maxWallMs,
        maxEvaluations: this.cfg.maxEvaluations,
        elitesPerGen: this.cfg.elite,
      }),
    }
  }

  /**
   * Run unit tests defined in the problem to validate the fitness function.
   * Returns an array of test results.
   */
  async runUnitTests(): Promise<
    Array<{
      name: string
      genome: T
      fitness: number
      passed: boolean
      error?: string
    }>
  > {
    if (!this.problem.unitTests || this.problem.unitTests.length === 0) {
      return []
    }

    return Promise.all(
      this.problem.unitTests.map(async (test) => {
        try {
          const fitness = await this.safeFitness(test.genome)

          // Check constraints
          const { exact, min, max, greaterThan, lessThan } = test.expect
          let passed = true
          let error: string | undefined

          if (exact !== undefined && fitness !== exact) {
            passed = false
            error = `Expected exactly ${exact}, got ${fitness}`
          }
          if (min !== undefined && fitness < min) {
            passed = false
            error = `Expected at least ${min}, got ${fitness}`
          }
          if (max !== undefined && fitness > max) {
            passed = false
            error = `Expected at most ${max}, got ${fitness}`
          }
          if (greaterThan !== undefined && fitness <= greaterThan) {
            passed = false
            error = `Expected greater than ${greaterThan}, got ${fitness}`
          }
          if (lessThan !== undefined && fitness >= lessThan) {
            passed = false
            error = `Expected less than ${lessThan}, got ${fitness}`
          }

          return {
            name: test.name,
            genome: test.genome,
            fitness,
            passed,
            error,
          }
        } catch (e) {
          return {
            name: test.name,
            genome: test.genome,
            fitness: -Infinity,
            passed: false,
            error: `Fitness evaluation failed: ${e instanceof Error ? e.message : String(e)}`,
          }
        }
      }),
    )
  }
}
